import {
  Collapse,
  Divider,
  Form,
  Input,
  Upload,
  Card,
  Select,
  Image,
  Typography,
  Tag,
  Flex,
  message,
  Modal,
  Dropdown,
  Button,
} from "antd";
import { useEffect, useState } from "react";
import { CreditSteps } from "./credit-steps";
import { PresupuestoIcon } from "../icon";
import {
  CheckCircleFilled,
  FileTextOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { axiosInstance, formatPrice } from "@/utilities";
import CreditCardForm from "./form-fiserv";
import { API_URL_ORDERS } from "@/services/XCONS_ENV";
import { t } from "i18next";

const { Panel } = Collapse;
const { Text } = Typography;

export interface CreditMemo {
  creditMemoId: string;
  status: string;
  document?: {
    number?: string;
    pointOfSale?: string;
  };
  receipts?: [
    {
      originalFilename?: string;
      receiptId?: string;
    },
  ];
  paymentMethod?: string;
}

export const CreditMemoPanel = ({
  memo,
  delivery,
  handlePreview,
  handleRemoveFile,
  paymentOptions,
  updateOrderData,
  amount,
  shipment,
  index,
  billingAddress,
  totalDeliveryGroups,
}: {
  memo: CreditMemo & { shipment?: any; amount?: number; shipmentName?: string };
  delivery: any;
  handlePreview: any;
  handleRemoveFile: any;
  paymentOptions: any;
  updateOrderData?: () => void;
  amount?: number;
  shipment?: any;
  index: number;
  billingAddress?: any;
  totalDeliveryGroups?: number;
}) => {
  // Construye el label del header según el alcance de la cancelación.
  // Lee `cancelationType` del metadata y el `deliveryGroup` ya resuelto por
  // el padre (que cae al source shipment si el canceled no lo tiene).
  // Si no hay `cancelationType` (creditmemos previos a esa convención) cae
  // al label genérico para no romper visualmente los pedidos viejos.
  const buildCreditMemoLabel = (): string => {
    const cancelationType = memo?.shipment?.metadata?.cancelationType;
    const deliveryGroup =
      (memo as any)?.resolvedDeliveryGroup ??
      memo?.shipment?.metadata?.deliveryGroup;
    const bulkDeliveryGroups: number[] | undefined =
      (memo as any)?.metadata?.bulkDeliveryGroups ??
      memo?.shipment?.metadata?.bulkDeliveryGroups;
    // memo.metadata puede venir como object (transformer) o como string
    // (cuando no pasó por plainToInstance). Parseamos defensivamente.
    const rawMemoMeta = (memo as any)?.metadata;
    const memoMetadata =
      typeof rawMemoMeta === "string"
        ? (() => {
            try {
              return JSON.parse(rawMemoMeta);
            } catch {
              return null;
            }
          })()
        : rawMemoMeta;

    // Shipping-only: la NC abarca solo el envío del shipment (los items
    // quedan intactos). metadata.shippingOnly se setea en
    // confirmShippingOnlyCancel del backend.
    if (memoMetadata?.shippingOnly === true) {
      return t("orders.label.creditnoteShippingOnly", {
        defaultValue: "Cancelación de envío",
      });
    }

    // Bulk: NC abarca varias entregas. `bulk_total` = todas las entregas
    // del pedido están canceladas. `bulk_partial` = alguna entrega ya
    // estaba entregada y se cancelaron las que faltaban.
    if (cancelationType === "bulk_total") {
      return t("orders.label.creditnoteBulkFullCancellation");
    }
    if (cancelationType === "bulk_partial" && bulkDeliveryGroups?.length) {
      return t("orders.label.creditnoteBulkPartialCancellation", {
        list: formatDeliveryGroupList(bulkDeliveryGroups),
      });
    }

    if (cancelationType === "total") {
      if (totalDeliveryGroups === 1) {
        return t("orders.label.creditnoteFullOrderCancellation");
      }
      if (deliveryGroup != null) {
        return t("orders.label.creditnoteDeliveryFullCancellation", {
          number: deliveryGroup,
        });
      }
    }

    if (cancelationType === "partial" && deliveryGroup != null) {
      return t("orders.label.creditnoteDeliveryPartialCancellation", {
        number: deliveryGroup,
      });
    }

    return `${t("orders.label.partiallycancelled")} ${index + 1}`;
  };

  // "3, 4 y 5" en es, "3, 4 and 5" en en. Para 1 solo elemento devuelve "3".
  const formatDeliveryGroupList = (groups: number[]): string => {
    const sorted = [...groups].sort((a, b) => a - b);
    if (sorted.length <= 1) return String(sorted[0] ?? "");
    const last = sorted[sorted.length - 1];
    const head = sorted.slice(0, -1).join(", ");
    const conn = t("common.and"); // "y" / "and"
    return `${head} ${conn} ${last}`;
  };

  const [selectedPayment, setSelectedPayment] = useState<string>(
    memo?.paymentMethod || "cash",
  );
  const [creditMemoFileLists, setCreditMemoFileLists] = useState<any[]>([]);
  const [creditMemoReceiptLists, setCreditMemoReceiptLists] = useState<any[]>(
    [],
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [canSendReview, setCanSendReview] = useState(false);
  const [canSendInvoice, setCanSendInvoice] = useState(false);
  const [loadingStep, setLoadingStep] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Collapse controlado: mantiene el panel abierto incluso si el parent
  // refetches y remonta el componente. Antes con defaultActiveKey el
  // dropdown se cerraba al pasar de Pending → Approved porque
  // updateOrderData re-fetchea y el remount no preservaba el estado.
  const panelKey = `credit-${memo.creditMemoId}`;
  const [activeKey, setActiveKey] = useState<string | string[]>(panelKey);

  const statusCredits = [
    "pending",
    "approved",
    "pending_payment",
    "payment_verified",
  ];

  const [creditMemoForm] = Form.useForm();

  // document
  const handleCreditMemoUpload = async (options: any, creditMemoId: string) => {
    const { file, onSuccess, onError } = options;
    const values = creditMemoForm.getFieldsValue([
      "note_credit_number",
      "note_credit_number_code",
    ]);

    try {
      const pointOfSale = parseInt(values.note_credit_number_code, 10);
      const number = parseInt(values.note_credit_number, 10);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("pointOfSale", pointOfSale.toString());
      formData.append("number", number.toString());

      await axiosInstance.post(
        `${API_URL_ORDERS}/credit-memos/${creditMemoId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setCreditMemoFileLists((prev: any) => [
        ...prev,
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          originFileObj: file,
        },
      ]);

      updateOrderData?.();

      message.success(t("common.messages.invoiceUploaded"));
    } catch (error) {
      message.error(t("common.messages.invoiceUploadFailed"));
    }
  };

  const handleBeforeUpload = (file: any) => {
    // Puedes cambiar el '15' por la cantidad de Megabytes máximos que desees permitir
    const limitMB = 15;
    const isLessThanLimit = file.size / 1024 / 1024 < limitMB;

    if (!isLessThanLimit) {
      message.error(
        `El archivo "${file.name}" excede el tamaño máximo permitido de ${limitMB}MB`,
      );
    }

    return isLessThanLimit ? true : Upload.LIST_IGNORE;
  };

  // receipt
  const handleCreditMemoReceiptUpload = async (
    options: any,
    creditMemoId: string,
  ) => {
    const { file, onSuccess, onError } = options;
    const values = creditMemoForm.getFieldsValue([
      "memo_receipt",
      "memo_amount",
    ]);

    try {
      const pointOfSale = parseInt(values.memo_receipt, 10);
      const number = parseInt(values.memo_amount, 10);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("pointOfSale", pointOfSale.toString());
      formData.append("number", number.toString());

      await axiosInstance.post(
        `${API_URL_ORDERS}/credit-memos/${creditMemoId}/receipts`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setCreditMemoReceiptLists((prev: any) => [
        ...prev,
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          originFileObj: file,
        },
      ]);

      updateOrderData?.();

      message.success(t("common.messages.invoiceUploaded"));
    } catch (error) {
      message.error(t("common.messages.invoiceUploadFailed"));
    }
  };

  // Borra el archivo (o un comprobante) de la nota de crédito en el servidor,
  // con la misma confirmación que factura y remito.
  const handleRemoveCreditMemoFile = (
    kind: "document" | "receipt",
    file: any,
    setList: (fn: (prev: any[]) => any[]) => void,
  ): Promise<boolean> => {
    const id =
      kind === "document"
        ? memo?.document && (memo.document as any).documentId
        : String(file.uid).split("-").pop();
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("orders.creditMemo.delete.confirm.title"),
        content: t("orders.creditMemo.delete.confirm.content"),
        okText: t("buttons.confirm"),
        cancelText: t("buttons.back"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(
              `${API_URL_ORDERS}/credit-memos/${
                kind === "document" ? "documents" : "receipts"
              }/${id}`,
            );
            setList((prev: any[]) => prev.filter((f) => f.uid !== file.uid));
            message.success(t("orders.creditMemo.delete.success"));
            updateOrderData?.();
            resolve(true);
          } catch (error: any) {
            if (error.message === "Insufficient permissions") {
              message.error(t("insufficient.permissions"));
            } else {
              message.error(t("orders.creditMemo.delete.failed"));
            }
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  // Corrige punto de venta y número del documento de la NC ya cargado, sin
  // volver a subir el PDF (xcons-docs#346). Se llama on blur de cada input.
  //
  // Ojo con el mapeo, que en la NC va al revés que en la factura y viene de
  // antes: `note_credit_number` es el NÚMERO y `note_credit_number_code` es el
  // PUNTO DE VENTA. Se respeta tal cual está para no cambiarle el significado
  // a lo ya cargado.
  //
  // El backend decide: si la NC dejó de estar `pending` o ya tiene
  // `magentoCreditMemoId`, contesta 409 y hay que emitir una nota nueva.
  const saveCreditMemoNumbers = async () => {
    const documentId = (memo?.document as any)?.documentId;
    if (!documentId) return;

    const v = creditMemoForm.getFieldsValue([
      "note_credit_number",
      "note_credit_number_code",
    ]);
    const number = (v.note_credit_number ?? "").toString().trim();
    const pointOfSale = (v.note_credit_number_code ?? "").toString().trim();

    const sinCambios =
      pointOfSale === ((memo?.document?.pointOfSale ?? "") as string) &&
      number === ((memo?.document?.number ?? "") as string);
    if (sinCambios) return;

    if (!/^\d+$/.test(pointOfSale) || !/^\d+$/.test(number)) {
      message.error(t("orders.documents.numbersOnlyDigits"));
      return;
    }

    try {
      await axiosInstance.patch(
        `${API_URL_ORDERS}/credit-memos/documents/${documentId}`,
        { pointOfSale, number },
      );
      message.success(t("orders.documents.numbersSaved"));
      updateOrderData?.();
    } catch (error: any) {
      // El 409 manda `message` como string; el 400 de validación, como array.
      const detalle = error?.response?.data?.message;
      message.error(
        (Array.isArray(detalle) ? detalle.join(" ") : detalle) ||
          t("orders.documents.numbersFailed"),
      );
      creditMemoForm.setFieldsValue({
        note_credit_number: memo?.document?.number,
        note_credit_number_code: memo?.document?.pointOfSale,
      });
    }
  };

  // documents
  const getAllCreditMemos = async (creditMemo: {
    creditMemoId: number;
    document: {
      originalFilename: string;
      documentId: number;
    };
  }) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_ORDERS}/credit-memos/documents/${creditMemo?.document?.documentId}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const file = {
        uid: creditMemo.creditMemoId.toString(),
        name: creditMemo.document.originalFilename || "Credit note",
        status: "done",
        url,
        type: res.data.type,
      };
      setCreditMemoFileLists([file]);
    } catch (error) {
      console.error(error);
    }
  };

  // receipts
  const getAllReceiptMemos = async (creditMemo: {
    creditMemoId: number;
    receipts?: Array<{
      originalFilename: string;
      receiptId: number;
    }>;
  }) => {
    try {
      if (!creditMemo?.receipts?.length) {
        setCreditMemoReceiptLists([]);
        return;
      }

      const receiptFiles = await Promise.all(
        creditMemo.receipts.map(async (receipt) => {
          try {
            const res = await axiosInstance.get(
              `${API_URL_ORDERS}/credit-memos/receipts/${receipt.receiptId}`,
              { responseType: "blob" },
            );

            const url = URL.createObjectURL(res.data);
            return {
              uid: `${creditMemo.creditMemoId}-${receipt.receiptId}`,
              name: receipt.originalFilename || `Receipt-${receipt.receiptId}`,
              status: "done" as const,
              url,
              type: res.data.type,
            };
          } catch (error) {
            console.error(`Error loading receipt ${receipt.receiptId}:`, error);
            return null;
          }
        }),
      );

      const validFiles = receiptFiles.filter(
        (file): file is NonNullable<typeof file> => file !== null,
      );
      setCreditMemoReceiptLists(validFiles);
    } catch (error) {
      console.error("Error in getAllReceiptMemos:", error);
      setCreditMemoReceiptLists([]);
    }
  };

  // steps changes
  const handleChangeCreditStep = async (status: string) => {
    setLoadingStep(true);
    try {
      const payload = {
        status: status,
        metadata: {},
      };

      await axiosInstance.patch(
        `${API_URL_ORDERS}/credit-memos/${memo.creditMemoId}`,
        payload,
      );

      const newStep = statusCredits.indexOf(status);
      if (newStep !== -1) {
        setCurrentStep(newStep);
      } else {
        console.error("Invalid status:", status);
      }

      updateOrderData?.();

      message.success(t("common.messages.paymentStatusUpdated"));
    } catch (error) {
      message.error(t("common.messages.paymentStatusUpdateFailed"));
    } finally {
      setLoadingStep(false);
    }
  };

  // (Re)envía la nota de crédito ya cargada a Magento, SIN cambiar su estado.
  const handleResendCreditMemo = async () => {
    if (!memo?.creditMemoId) return;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/credit-memos/${memo.creditMemoId}/resend`,
      );
      message.success(t("orders.creditMemo.resendSuccess"));
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || t("orders.creditMemo.resendError"),
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // update states and visibility
  useEffect(() => {
    if (memo?.document) {
      getAllCreditMemos(memo as any);
      setCanSendReview(true);
      setCurrentStep(statusCredits.indexOf(memo.status));

      creditMemoForm.setFieldsValue({
        note_credit_number: memo.document.number,
        note_credit_number_code: memo.document.pointOfSale,
      });
    }
    if (memo?.receipts?.length) {
      getAllReceiptMemos(memo as any);
      setCanSendInvoice(true);
    } else {
      setCreditMemoReceiptLists([]);
      setCanSendInvoice(false);
    }

    setSelectedPayment(memo?.paymentMethod || "");
  }, [memo]);

  // Para NCs bulk / solo-envío el monto vive en memo.metadata.grandTotal
  // (lo escribe bulkCancelPendingConfirm / confirmShippingOnlyCancel).
  // Para NCs individuales sigue en memo.shipment.metadata.grandTotal.
  const parsedMemoMeta = (() => {
    const raw = (memo as any)?.metadata;
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return raw;
  })();
  const grandTotal =
    memo?.amount ??
    parsedMemoMeta?.grandTotal ??
    memo?.shipment?.metadata?.grandTotal;

  return (
    // El id es para el atajo desde el flujo de la entrega cancelada.
    <div id={`credit-memo-${memo.creditMemoId}`}>
      <Collapse
        className="custom-header-collapse"
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        // El margen va afuera del borde: adentro, con la nota cerrada, quedaba como una franja
        // blanca debajo del encabezado (Diseño, 2026-10-19).
        style={{ marginTop: 16, marginBottom: 16 }}
      >
        <Panel
          key={panelKey}
          header={
            <Flex gap={6} align="center">
              <FileTextOutlined style={{ color: "white" }} />
              <Text strong style={{ color: "white" }}>
                {t("orders.label.creditnote")}
              </Text>
              <Divider
                type="vertical"
                style={{ height: 12, background: "white" }}
              />
              <Text strong style={{ color: "white" }}>
                {buildCreditMemoLabel()}
              </Text>
            </Flex>
          }
          extra={
            <Flex align="center" gap={16}>
              <Text strong style={{ color: "white" }}>
                Balance:{" "}
                {memo.status !== "payment_verified"
                  ? "-" + formatPrice(grandTotal)
                  : "$0.00"}
              </Text>

              {memo?.status === "payment_verified" && (
                <Flex>
                  <CheckCircleFilled style={{ transform: "scale(1.5)" }} />
                </Flex>
              )}
              {/* <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "1",
                    label: (
                      <Text type="danger">Cancel all pendings deliveries</Text>
                    ),
                    onClick: (e) => {
                      e.domEvent.stopPropagation();
                      console.log("Cancel all pendings deliveries??????");
                    },
                  },
                ],
              }}
            >
              <Button
                type="link"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
                style={{ color: "white" }}
              />
            </Dropdown> */}
            </Flex>
          }
        >
          <CreditSteps
            loadingStep={loadingStep}
            currentStep={currentStep}
            memo={memo}
            canSendReview={canSendReview}
            canSendInvoice={canSendInvoice}
            handleChangeStep={handleChangeCreditStep}
            delivery={delivery}
            shipmentInfo={{
              id: memo?.shipment?.shipmentId,
              name: memo?.shipmentName,
              amount: Number(memo?.amount ?? 0),
            }}
            entregas={(memo as any)?.entregasCanceladas}
            cancelacionTotal={
              memo?.shipment?.metadata?.cancelationType === "bulk_total"
            }
          />

          <Card className="tarjeta-columnas">
            <Flex justify="space-between" style={{ width: "100%" }}>
              {/* COLUMNA IZQUIERDA */}
              <Flex
                vertical
                className="tarjeta-columnas__columna"
                style={{ flex: 1 }}
              >
                <Flex justify="space-between" align="center">
                  <Flex
                    align="start"
                    gap={8}
                    vertical
                    style={{ width: "100%" }}
                  >
                    <Flex align="center" gap={8} style={{ width: "100%" }}>
                      <PresupuestoIcon />
                      <Flex justify="space-between" style={{ width: "99%" }}>
                        <Text strong>
                          {t("orders.label.creditnote")}{" "}
                          {memo?.document?.pointOfSale &&
                          memo?.document?.number ? (
                            <>{`${memo.document.pointOfSale} - ${memo.document.number}`}</>
                          ) : (
                            <Text
                              italic
                              style={{ color: "#FF8F1F", marginLeft: 6 }}
                            >
                              {t("orders-label.pending")}
                            </Text>
                          )}
                        </Text>

                        <Text strong>-{formatPrice(grandTotal)}</Text>
                      </Flex>
                    </Flex>
                    <Flex style={{ margin: "16px 0 8px" }}>
                      <span>{`${billingAddress?.billingType} ${billingAddress?.vatId} | ${billingAddress?.street}`}</span>
                    </Flex>
                  </Flex>
                </Flex>

                <Flex vertical style={{ marginTop: 16 }}>
                  <Form
                    form={creditMemoForm}
                    layout="vertical"
                    id={`documents-${memo.creditMemoId}`}
                  >
                    {/* Con el documento ya cargado los campos desaparecían de la
                        pantalla: no había forma de corregir un dedazo sin borrar
                        el documento. Ahora se muestran siempre y, cuando ya hay
                        documento, cada uno guarda al salir del campo
                        (xcons-docs#346). */}
                    <Flex gap={8} align="end">
                      <Form.Item
                        label={<Text type="secondary">Note credit number</Text>}
                        name="note_credit_number"
                        rules={[{ required: true }]}
                      >
                        <Input
                          placeholder="Ex: 00001"
                          onBlur={
                            memo?.document ? saveCreditMemoNumbers : undefined
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name="note_credit_number_code"
                        rules={[{ required: true }]}
                      >
                        <Input
                          placeholder="Ex: 00036548"
                          onBlur={
                            memo?.document ? saveCreditMemoNumbers : undefined
                          }
                        />
                      </Form.Item>
                    </Flex>
                    <Form.Item
                      label={
                        <Text type="secondary">{t("orders.tag.invoice")}</Text>
                      }
                      name="invoice_upload"
                      rules={[{ required: true }]}
                    >
                      <Upload
                        listType="picture-card"
                        accept="image/*,application/pdf"
                        customRequest={(options) =>
                          handleCreditMemoUpload(options, memo.creditMemoId)
                        }
                        onPreview={(file) =>
                          handlePreview(file, setPreviewImage, setPreviewOpen)
                        }
                        fileList={creditMemoFileLists}
                        onRemove={(file) =>
                          handleRemoveCreditMemoFile(
                            "document",
                            file,
                            setCreditMemoFileLists,
                          )
                        }
                        maxCount={1}
                        beforeUpload={handleBeforeUpload}
                      >
                        + {t("buttons.upload")}
                      </Upload>
                    </Form.Item>
                  </Form>
                </Flex>

                {/* Reenviar NC a Magento (no cambia el estado). Solo cuando ya
                  fue enviada (status posterior a pending y documento cargado).
                  Secundario, alineado a la derecha. */}
                {memo?.status !== "pending" && !!memo?.document && (
                  <Flex justify="end" style={{ marginTop: 16 }}>
                    <Button
                      size="small"
                      loading={loadingStep}
                      onClick={handleResendCreditMemo}
                    >
                      {t("orders.creditMemo.resend")}
                    </Button>
                  </Flex>
                )}
              </Flex>

              {/* COLUMNA DERECHA */}
              <Flex
                vertical
                className="tarjeta-columnas__columna"
                style={{ flex: 1 }}
                gap={8}
              >
                {memo?.status !== "pending" && (
                  <>
                    <Flex justify="space-between" align="center">
                      <Select
                        value={selectedPayment}
                        onChange={setSelectedPayment}
                        style={{ width: 250 }}
                        options={paymentOptions}
                        disabled
                      />
                      <Text
                        strong
                        style={{
                          color:
                            memo?.status !== "payment_verified"
                              ? "#FF8F1F"
                              : undefined,
                        }}
                      >
                        {formatPrice(grandTotal)}
                      </Text>
                    </Flex>

                    <Form
                      form={creditMemoForm}
                      layout="vertical"
                      id={`receipts-${memo.creditMemoId}`}
                    >
                      {!memo?.receipts?.length && (
                        <Flex gap={24}>
                          <Form.Item
                            label={<Text type="secondary">Receipt</Text>}
                            name="memo_receipt"
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>

                          <Form.Item
                            label={<Text type="secondary">Amount</Text>}
                            name="memo_amount"
                            rules={[{ required: true }]}
                          >
                            <Input disabled defaultValue={grandTotal} />
                          </Form.Item>
                        </Flex>
                      )}

                      <Form.Item
                        label={
                          <Text type="secondary">
                            {t("orders.label.paymentreceipt")}
                          </Text>
                        }
                        name="receipt_upload"
                        rules={[{ required: true }]}
                      >
                        <Upload
                          listType="picture-card"
                          accept="image/*,application/pdf"
                          customRequest={(options) =>
                            handleCreditMemoReceiptUpload(
                              options,
                              memo.creditMemoId,
                            )
                          }
                          onPreview={(file) =>
                            handlePreview(file, setPreviewImage, setPreviewOpen)
                          }
                          fileList={creditMemoReceiptLists}
                          onRemove={(file) =>
                            handleRemoveCreditMemoFile(
                              "receipt",
                              file,
                              setCreditMemoReceiptLists,
                            )
                          }
                          disabled={currentStep >= 2}
                          beforeUpload={handleBeforeUpload}
                        >
                          + {t("buttons.upload")}
                        </Upload>
                      </Form.Item>
                    </Form>
                  </>
                )}
                <Image
                  style={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) =>
                      !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              </Flex>
            </Flex>
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};
